import React from "react";
import { Box, Typography } from "@mui/material";
import { Storage, CloudUpload, Security, Backup } from "@mui/icons-material";

/**
 * Quick stats row for site metrics — flat bordered cards matching modern UI
 */
const SiteStats = ({ site }) => {
  const formatStorage = (mb) => {
    if (!mb || mb === "0") return "0 MB";
    const num = parseFloat(mb);
    if (num < 1024) return `${num.toFixed(1)} MB`;
    return `${(num / 1024).toFixed(2)} GB`;
  };

  const formatBandwidth = (gb) => {
    if (!gb || gb === "0") return "0 GB";
    const num = parseFloat(gb);
    return `${num.toFixed(2)} GB`;
  };

  const sslActive =
    site?.sslEnabled || process.env.REACT_APP_SSL_ENABLED === "true";

  const formatLastBackup = (date) => {
    if (!date) return "Never";
    const backupDate = new Date(date);
    const now = new Date();
    const diffInHours = Math.floor((now - backupDate) / (1000 * 60 * 60));
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return "1 day ago";
    return `${diffInDays} days ago`;
  };

  const stats = [
    {
      icon: <Storage sx={{ fontSize: 20 }} />,
      value: formatStorage(site?.storageUsedMB),
      label: "Storage",
      color: "#10b981",
      bg: "#d1fae5",
    },
    {
      icon: <CloudUpload sx={{ fontSize: 20 }} />,
      value: formatBandwidth(site?.bandwidthUsedGB),
      label: "Bandwidth",
      color: "#3b82f6",
      bg: "#dbeafe",
    },
    {
      icon: <Security sx={{ fontSize: 20 }} />,
      value: sslActive ? "Enabled" : "Disabled",
      label: "SSL",
      color: sslActive ? "#10b981" : "#f59e0b",
      bg: sslActive ? "#d1fae5" : "#fef3c7",
    },
    {
      icon: <Backup sx={{ fontSize: 20 }} />,
      value: formatLastBackup(site?.lastBackupAt),
      label: "Last Backup",
      color: "#6366f1",
      bg: "#e0e7ff",
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          sm: "repeat(4, 1fr)",
        },
        gap: 1.5,
        mb: 3,
      }}
    >
      {stats.map((stat, index) => (
        <Box
          key={index}
          sx={{
            p: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            transition: "all 0.15s",
            "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: stat.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: stat.color,
              flexShrink: 0,
            }}
          >
            {stat.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} noWrap>
              {stat.value}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
            >
              {stat.label}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SiteStats;
